import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./motion";
import { IMG, CONTACT } from "./data";
import { MapPin, Phone, Mail, Clock, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "", consent: false };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Ihre Anfrage wurde übermittelt. Wir melden uns innert 24 Stunden.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Übermittlung fehlgeschlagen. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full rounded-sm border border-white/10 bg-[#0d0d0f] px-4 py-3.5 text-sm text-white placeholder:text-[#8a919e] outline-none transition-colors duration-300 focus:border-[#4fd1c5]";

  return (
    <section id="kontakt" className="relative overflow-hidden bg-[#0d0d0f] py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-16 flex items-center gap-4">
          <span className="h-px w-12 bg-[#4fd1c5]" />
          <span className="font-mono-spc text-[11px] uppercase tracking-[0.35em] text-[#4fd1c5]">Kontakt</span>
        </div>

        <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">
          {/* Left: form */}
          <div>
            <Reveal>
              <h2 className="mb-8 font-head text-4xl font-light uppercase leading-[1.05] tracking-tighter text-metal sm:text-5xl lg:text-6xl">
                Sprechen wir über Ihr Objekt.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mb-10 max-w-lg font-light leading-relaxed text-[#c0c5ce]/70">
                Ob wiederkehrende Unterhaltsreinigung oder einmaliger Industrieeinsatz — senden Sie uns Ihre
                Anfrage. Wir melden uns innert 24 Stunden mit einer unverbindlichen Offerte.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <form data-testid="contact-form" onSubmit={submit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="form-name" className={field} placeholder="Name *" value={form.name} onChange={set("name")} required minLength={2} />
                  <input data-testid="form-email" type="email" className={field} placeholder="E-Mail *" value={form.email} onChange={set("email")} required />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="form-phone" className={field} placeholder="Telefon" value={form.phone} onChange={set("phone")} />
                  <input data-testid="form-subject" className={field} placeholder="Betreff" value={form.subject} onChange={set("subject")} />
                </div>
                <textarea data-testid="form-message" className={`${field} min-h-[140px] resize-y`} placeholder="Ihre Nachricht *" value={form.message} onChange={set("message")} required minLength={5} />

                <div className="rounded-sm border border-white/10 bg-[#111214] p-5">
                  <p className="mb-4 text-[13px] font-light leading-relaxed text-[#c0c5ce]/70">
                    Wenn Sie uns über das Kontaktformular kontaktieren, verarbeiten wir die von Ihnen angegebenen
                    personenbezogenen Daten ausschliesslich zur Bearbeitung Ihrer Anfrage und für eine eventuelle
                    spätere Kommunikation. Die Übermittlung Ihrer Daten erfolgt freiwillig. Ihre Daten werden
                    vertraulich behandelt und ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben,
                    sofern hierfür keine gesetzliche Verpflichtung besteht oder die Weitergabe zur Bearbeitung
                    Ihrer Anfrage erforderlich ist. Weitere Informationen finden Sie in unserer
                    Datenschutzerklärung.
                  </p>
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      data-testid="form-consent"
                      type="checkbox"
                      checked={form.consent}
                      onChange={set("consent")}
                      className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-[#4fd1c5]"
                    />
                    <span className="text-[13px] font-light leading-relaxed text-[#c0c5ce]/80">
                      Ich habe die Datenschutzerklärung gelesen und bin mit der Verarbeitung meiner
                      personenbezogenen Daten zum Zweck der Bearbeitung meiner Anfrage einverstanden. *
                    </span>
                  </label>
                </div>

                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 rounded-sm bg-[#4fd1c5] px-8 py-4 font-mono-spc text-[11px] uppercase tracking-[0.22em] text-[#0d0d0f] transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? "Wird gesendet" : "Anfrage senden"}
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                  ) : (
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                  )}
                </button>
              </form>
            </Reveal>
          </div>

          {/* Right: details + Basel image */}
          <div className="flex flex-col gap-6">
            <div className="grid gap-px overflow-hidden rounded-sm border border-white/10 bg-white/10 sm:grid-cols-2">
              <Detail icon={MapPin} label="Adresse" testid="contact-address">
                {CONTACT.address}
                <br />
                {CONTACT.city}, {CONTACT.country}
              </Detail>
              <Detail icon={Phone} label="Telefon" testid="contact-phone">
                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-[#4fd1c5]">
                  {CONTACT.phone}
                </a>
              </Detail>
              <Detail icon={Mail} label="E-Mail" testid="contact-email">
                <a href={`mailto:${CONTACT.email}`} className="break-all transition-colors hover:text-[#4fd1c5]">
                  {CONTACT.email}
                </a>
              </Detail>
              <Detail icon={Clock} label="Erreichbarkeit" testid="contact-hours">
                Mo – Sa · 07:00 – 19:00
                <br />
                Industrie: nach Vereinbarung
              </Detail>
            </div>

            <Reveal delay={0.15} className="flex-1">
              <div className="group relative h-full min-h-[280px] overflow-hidden rounded-sm border border-white/10">
                <img
                  src={IMG.basel}
                  alt="Basel, Schweiz"
                  className="h-full w-full object-cover grayscale transition-[filter,transform] duration-[1000ms] ease-out group-hover:grayscale-0 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f] via-[#0d0d0f]/20 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <span className="font-mono-spc text-[11px] uppercase tracking-[0.3em] text-[#4fd1c5]">Standort</span>
                  <p className="mt-2 font-head text-3xl font-light text-white">Basel · Schweiz</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Detail({ icon: Icon, label, children, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col gap-3 bg-[#111214] p-8">
      <Icon className="h-5 w-5 text-[#4fd1c5]" strokeWidth={1.5} />
      <span className="font-mono-spc text-[10px] uppercase tracking-[0.3em] text-[#8a919e]">{label}</span>
      <p className="font-light leading-relaxed text-[#c0c5ce]">{children}</p>
    </div>
  );
}
